/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdNavigation — breadcrumb chrome rendered as a `<wa-breadcrumb>` list.
 * Maintains a `crumbs` attribute (comma-separated string) that mirrors
 * an internal `_crumbs: string[]` stack. Public API:
 *
 *   - `addLayer(label)` / `removeLastLayer()` — push/pop the stack
 *   - `setCrumbs(string | string[])` / `clear()` — bulk operations
 *   - `getCrumbs()` — defensive copy of the stack
 *
 * Every mutation re-renders and dispatches a typed `td-navigation`
 * CustomEvent with the new state. Runtime type guards in `addLayer` /
 * `setCrumbs` remain in place so .js call sites that pass non-string
 * values (still possible at the boundary) are tolerated rather than
 * crashing.
 *
 * Lit light-DOM component (#206). Crumb labels reach the template via
 * `${...}` text-bindings, never via interpolated HTML — server- or
 * user-supplied labels stay opaque to the parser.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { t, LocaleController } from '../services/i18n.js';

/** Detail payload of the `td-navigation` CustomEvent dispatched on every mutation. */
export interface TdNavigationEventDetail {
  crumbs: string[];
  crumbsString: string;
}

@customElement('td-navigation')
export class TdNavigation extends LitElement {
  @state() private _crumbs: string[] = [];
  private _crumbsAttribute: string = '';

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  static override get observedAttributes(): string[] {
    return ['crumbs'];
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    // Intentionally NOT calling `super.attributeChangedCallback` — the
    // `crumbs` attribute is owned by the custom getter/setter pair below,
    // not by `@property`. Routing through super would try to look up a
    // non-existent property descriptor.
    if (name === 'crumbs' && oldValue !== newValue) {
      this._crumbsAttribute = newValue || '';
      this._crumbs = this._parseCrumbs(this._crumbsAttribute);
      this._dispatchChangeEvent();
    }
  }

  get crumbs(): string {
    return this._crumbsAttribute;
  }

  set crumbs(value: string | null | undefined) {
    this.setAttribute('crumbs', value || '');
  }

  /**
   * Add a new layer to the breadcrumb. Non-string / empty values are
   * silently ignored — the runtime guard tolerates `.js` callers that
   * pass `null` / `undefined` / numbers (the test file exercises all
   * four).
   */
  addLayer(label: string): void {
    if (label && typeof label === 'string') {
      this._crumbs = [...this._crumbs, label];
      this._updateCrumbsAttribute();
      this._dispatchChangeEvent();
    }
  }

  /**
   * Remove the last layer from the breadcrumb.
   * @returns The removed label, or `undefined` if the stack was empty.
   */
  removeLastLayer(): string | undefined {
    if (this._crumbs.length === 0) return undefined;
    const next = this._crumbs.slice(0, -1);
    const removed = this._crumbs[this._crumbs.length - 1];
    this._crumbs = next;
    this._updateCrumbsAttribute();
    this._dispatchChangeEvent();
    return removed;
  }

  /** Get the current breadcrumb items as a defensive-copy array. */
  getCrumbs(): string[] {
    return [...this._crumbs];
  }

  /**
   * Set the breadcrumb items from an array or comma-separated string.
   * Invalid input (non-string non-array) is ignored — see runtime
   * guard rationale on `addLayer`.
   */
  setCrumbs(crumbs: string | string[]): void {
    if (Array.isArray(crumbs)) {
      this._crumbs = crumbs.filter(
        (crumb): crumb is string => typeof crumb === 'string'
      );
    } else if (typeof crumbs === 'string') {
      this._crumbs = this._parseCrumbs(crumbs);
    } else {
      // Invalid input, don't change anything.
      return;
    }

    this._updateCrumbsAttribute();
    this._dispatchChangeEvent();
  }

  /** Clear all breadcrumb items. */
  clear(): void {
    this._crumbs = [];
    this._updateCrumbsAttribute();
    this._dispatchChangeEvent();
  }

  private _parseCrumbs(crumbsString: string): string[] {
    if (!crumbsString || typeof crumbsString !== 'string') {
      return [];
    }
    return crumbsString
      .split(',')
      .map(crumb => crumb.trim())
      .filter(crumb => crumb.length > 0);
  }

  private _updateCrumbsAttribute(): void {
    this._crumbsAttribute = this._crumbs.join(', ');
    this.setAttribute('crumbs', this._crumbsAttribute);
  }

  private _dispatchChangeEvent(): void {
    this.dispatchEvent(
      new CustomEvent<TdNavigationEventDetail>('td-navigation', {
        detail: {
          crumbs: this._crumbs,
          crumbsString: this._crumbsAttribute,
        },
        bubbles: true,
        cancelable: false,
      })
    );
  }

  override render(): TemplateResult {
    return html`
      <wa-breadcrumb class="wa-body-s">
        <wa-breadcrumb-item>${t('nav.home')}</wa-breadcrumb-item>
        ${this._crumbs.map(
          crumb => html`<wa-breadcrumb-item>${crumb}</wa-breadcrumb-item>`
        )}
      </wa-breadcrumb>
    `;
  }
}
